import { PlanetClass, PlanetFilterInput } from '@apeiron/planet'
import { Box, styled, Typography } from '@mui/material'
import {
  ChipIcon,
  ChipLabel,
  ChipToggleButton,
  ChipToggleButtonGroup
} from '@src/components/share/filter/ChipToggle'
import {
  FilterSection,
  FilterSectionContent,
  FilterSectionHeader,
  FilterSectionTitle
} from '@src/components/share/filter/FilterSection'
import ResetFilter from '@src/components/share/filter/ResetFilter'
import { usePlanetClassContext } from '@src/contexts/planet/PlanetClassContext'
import { usePlanetTypeContext } from '@src/contexts/planet/PlanetTypeContext'
import { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const Content = styled(FilterSectionContent)`
  &.MuiAccordionDetails-root {
    padding-left: 20px;
    padding-right: 10px;
    padding-bottom: 9px;
  }
`

const FilterLabel = styled(Box)`
  font-size: 16px;
  line-height: 18px;
  color: #333333;
  margin-top: 6px;
  margin-bottom: 6px;
`

const PlanetTypeGroups = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const GroupName = styled(Typography)`
  font-size: 14px;
  font-weight: 400;
  color: #666666;
`

const PlanetClassAndTypeFilter: FC<Props> = (props: Props) => {
  const { className, filterOptions, onFilterOptionsChange } = props

  const { planetTypeGroupData } = usePlanetTypeContext()

  const { planetClasses } = usePlanetClassContext()

  const { t } = useTranslation()

  const planetClassIDs = filterOptions.planetClassID ?? []

  const planetTypes = filterOptions.planetType ?? []

  const handleOnClassChange = useCallback(
    (_: any, input: number[]) => {
      onFilterOptionsChange({
        ...filterOptions,
        planetClassID: input.length === 0 ? undefined : input
      })
    },
    [filterOptions, onFilterOptionsChange]
  )

  const handleOnTypeChange = useCallback(
    (_: any, input: string[]) => {
      onFilterOptionsChange({
        ...filterOptions,
        planetType: input.length === 0 ? undefined : input
      })
    },
    [filterOptions, onFilterOptionsChange]
  )

  const handleOnReset = useCallback(() => {
    onFilterOptionsChange({
      ...filterOptions,
      planetClassID: undefined,
      planetType: undefined
    })
  }, [filterOptions, onFilterOptionsChange])

  const numFilters = planetClassIDs.length + planetTypes.length

  return (
    <FilterSection className={className} defaultExpanded>
      <FilterSectionHeader>
        <FilterSectionTitle>
          {t('filter.sections.main.title')}
        </FilterSectionTitle>
        <ResetFilter filterCount={numFilters} onReset={handleOnReset} />
      </FilterSectionHeader>
      <Content>
        <FilterLabel>
          {t('filter.sections.main.avatar_class.label')}
        </FilterLabel>
        <ChipToggleButtonGroup
          value={planetClassIDs}
          onChange={handleOnClassChange}
        >
          {planetClasses.map((planetClass: PlanetClass) => (
            <ChipToggleButton key={planetClass.type} value={planetClass.type}>
              <ChipIcon src={planetClass.image} />
              <ChipLabel>{planetClass.name}</ChipLabel>
            </ChipToggleButton>
          ))}
        </ChipToggleButtonGroup>

        <FilterLabel>{t('filter.sections.main.planet_type')}</FilterLabel>
        <PlanetTypeGroups>
          {planetTypeGroupData.map(group => {
            return (
              <Box key={group.name}>
                <GroupName>{t(group.name)}</GroupName>
                <ChipToggleButtonGroup
                  value={planetTypes}
                  onChange={handleOnTypeChange}
                >
                  {group.planetTypes.map(type => (
                    <ChipToggleButton key={type.name} value={type.name}>
                      <ChipIcon src={type.planetTypeIcon} />
                      <ChipLabel>{type.name}</ChipLabel>
                    </ChipToggleButton>
                  ))}
                </ChipToggleButtonGroup>
              </Box>
            )
          })}
        </PlanetTypeGroups>
      </Content>
    </FilterSection>
  )
}

type Props = {
  className?: string
  filterOptions: PlanetFilterInput
  onFilterOptionsChange: (input: PlanetFilterInput) => void
}

export default PlanetClassAndTypeFilter
